import { DateTime } from 'luxon';
import {
    all,
    call,
    put,
    select,
    takeEvery,
    takeLatest,
} from 'redux-saga/effects';
import { AllowedUsersModel } from '../../authentication/models/AllowedUsersModel';
import { sharedCommonActions } from '../../common/redux/SharedCommonActions';
import { getBusinessLine } from '../../core/configs/BusinessLineConfig';
import { getCurrentUserResponse } from '../../core/models/GetCurrentUser';
import TerranisBusinessLines from '../../core/models/TerranisBusinessLines';
import { firestoreAuthService } from '../../core/services/FirestoreService';
import { getErrorMessage, logger } from '../../core/services/LogService';
import { getProducerRefFromFramerName } from '../../fields/helpers/FieldHelper';
import ProcessingExecutionMode from '../../processing/models/ProcessingExecutionMode';
import { ParentReportJob } from '../models/ParentReportJob';
import PostBatchReportRequest from '../models/PostBatchReportRequest';
import PostReportRequest from '../models/PostReportRequest';
import { ProducerReportJob } from '../models/ProducerReportJob';
import { ReportConfiguration } from '../models/ReportConfigurationModel';
import ReportJob from '../models/ReportJob';
import ReportJobType from '../models/ReportJobType';
import { downloadFilesAsZip } from '../services/GenerateZipFileService';
import pixagriReportApiService from '../services/PixagriReportApiService';
import ReportState from './ReportState';
import actions, {
    DOWNLOAD_ZIP_FILE,
    DownloadZipFileAction,
    GENERATE_CAMPAIGN_REPORT,
    GENERATE_NOTIFICATION,
    GENERATE_REPORT,
    GenerateCampaignReportAction,
    GenerateNotificationAction,
    GenerateReportAction,
    GET_PARENT_REPORT_JOBS,
    GET_PRODUCER_REPORT_JOB,
    GetProducerReportJobAction,
    LOAD_REPORTS,
    LOAD_REPORTS_CONFIG,
    LoadReportsAction,
    RETRY_REPORT_GENERATION,
    RetryReportGenerationAction,
    SET_SELECTED_REPORT,
    sharedReportActions,
} from './SharedReportActions';

export function* sharedReportsSagas() {
    yield all([
        takeEvery(LOAD_REPORTS_CONFIG, loadReportsConfig),
        takeLatest(LOAD_REPORTS, loadReports),
        takeEvery(SET_SELECTED_REPORT, setSelectedReport),
        takeEvery(RETRY_REPORT_GENERATION, retryReportGeneration),
        takeEvery(GENERATE_NOTIFICATION, generateNotification),
        takeLatest(GET_PRODUCER_REPORT_JOB, getProducerReportJob),
        takeEvery(GET_PARENT_REPORT_JOBS, getParentReportJobs),
        takeEvery(GENERATE_CAMPAIGN_REPORT, generateCampaignReport),
        takeEvery(DOWNLOAD_ZIP_FILE, downloadZipFile),
        takeEvery(GENERATE_REPORT, generateReport),
    ]);
}
function* downloadZipFile(action: DownloadZipFileAction) {
    try {
        yield put(sharedCommonActions.fetchStart('downloadZipFile'));

        const { parent_report_jobs }: ReportState = yield select(
            (state) => state.report,
        );
        const { parentJobs } = action.payload;
        const urls: string[] = [];
        parentJobs.forEach((id) => {
            const parentReportJob = Object.values(parent_report_jobs || {}).find(
                (prj) => prj.id === id,
            );
            if (!parentReportJob) {
                logger.error(`Parent report job with id ${id} not found`);
                return;
            }
            if (parentReportJob && parentReportJob.report_jobs) {
                const reportJobs = parentReportJob.report_jobs;
                if (reportJobs) {

                    Object.values(reportJobs).forEach((report) => {
                        if (report.public_url) {
                            urls.push(report.public_url);
                        } else {
                            logger.error(
                                `No public url for report job ${report.id} of parent job ${id}`,
                            );
                        }
                    });
                }
            } else {
                logger.error(`Parent report job with id ${id} not found`);
            }
        });
        yield call(
            downloadFilesAsZip,
            urls,
            `export_reports_${DateTime.now().toSQLDate()}.zip`,
        );

        yield put(sharedCommonActions.fetchSuccess('downloadZipFile'));
    } catch (error: any) {
        const errorMessage = getErrorMessage(error);
        logger.error(error);
        yield put(sharedCommonActions.fetchError('downloadZipFile', errorMessage));
    }
}
function* generateCampaignReport(action: GenerateCampaignReportAction) {
    try {
        yield put(sharedCommonActions.fetchStart('generateCampaignReport'));
        const { token }: getCurrentUserResponse = yield call(
            firestoreAuthService.getCurrentUser,
        );
        const { farmersList }: { farmersList: AllowedUsersModel[] | null } =
            yield select((state) => state.fields);
        const reportState: ReportState = yield select((state) => state.report);
        const { campaignId, type, producers, mode } = action.payload;
        const config = Object.values(reportState.config || {}).find(
            (conf) => conf.type === type,
        );
        if (!config) {
            throw new Error('Report config not found');
        }

        const users: { user_id: string; farmer_id: number | null }[] = [];
        farmersList?.forEach((farmer) => {
            const producer_ref = getProducerRefFromFramerName(farmer.farmer_name);
            if (producers?.includes(producer_ref)) {
                users.push({
                    user_id: farmer.uid || '',
                    farmer_id: farmer.farmer_id
                        ? parseFloat(farmer.farmer_id as any)
                        : null,
                });
            }
        });
        const request: PostBatchReportRequest<any> = {
            campaign_id: campaignId,
            business_line: TerranisBusinessLines.AGRI,
            report_config_ref: config.ref as ReportJobType,
            type: type,
            mode: mode ?? ProcessingExecutionMode.ASYNCHRONOUS,
            users: users,
            data: {},
        };
        const res: ParentReportJob = yield call(
            pixagriReportApiService.postBatchReport.bind(pixagriReportApiService),
            token.token,
            request,
        );

        yield put(
            sharedReportActions.getParentReportJobs(parseFloat(campaignId), type),
        );
        yield put(sharedCommonActions.fetchSuccess('generateCampaignReport'));
    } catch (error: any) {
        const errorMessage = getErrorMessage(error);
        logger.error(error);
        yield put(
            sharedCommonActions.fetchError('generateCampaignReport', errorMessage),
        );
    }
}
function* generateReport(action: GenerateReportAction) {
    try {
        yield put(sharedCommonActions.fetchStart('generateReport'));
        const { token }: getCurrentUserResponse = yield call(
            firestoreAuthService.getCurrentUser,
        );
        const { farmersList }: { farmersList: AllowedUsersModel[] | null } =
            yield select((state) => state.fields);
        const reportState: ReportState = yield select((state) => state.report);
        const { campaign_id, reportJobType, mode } = action.payload;
        const config = Object.values(reportState.config || {}).find(
            (conf) => conf.type === reportJobType,
        );
        if (!config) {
            throw new Error('Report config not found');
        }
        const farmer = farmersList?.find(
            (farmer) => farmer.farmer_id === action.payload.farmer_id,
        );
        const request: PostReportRequest<any> = {
            campaign_id: campaign_id,
            business_line: TerranisBusinessLines.AGRI,
            report_config_ref: config.ref as ReportJobType,
            mode: mode ?? ProcessingExecutionMode.ASYNCHRONOUS,
            field_id: action.payload.field_id,
            data: {
                farmer_id: action.payload.farmer_id ?? null,
            },
            distributor_ref: 'TERRANIS',
            organization_ref: getProducerRefFromFramerName(farmer?.farmer_name || ''),
            producer_ref: getProducerRefFromFramerName(farmer?.farmer_name || ''),
            lang: 'fr',
        };
        const res: ReportJob = yield call(
            pixagriReportApiService.handleReportRequest.bind(pixagriReportApiService),
            token.token,
            request,
        );
        yield call(
            downloadFilesAsZip,
            [res.public_url || ''],
            `export_reports_${DateTime.now().toSQLDate()}.zip`,
        );
        yield put(sharedCommonActions.fetchSuccess('generateReport'));
    } catch (error: any) {
        const errorMessage = getErrorMessage(error);
        logger.error(error);
        yield put(sharedCommonActions.fetchError('generateReport', errorMessage));
    }
}
function* getParentReportJobs(action: GetProducerReportJobAction) {
    try {
        yield put(sharedCommonActions.fetchStart('getParentReportJobs'));
        const { token }: getCurrentUserResponse = yield call(
            firestoreAuthService.getCurrentUser,
        );

        const parentReportJobs: { [id: string]: ParentReportJob } = yield call(
            pixagriReportApiService.getParentReportJobs.bind(pixagriReportApiService),
            token.token,
            action.payload.campaignId,
            action.payload.type,
        );
        yield put(sharedReportActions.getParentReportJobsSuccess(parentReportJobs));
        yield put(sharedCommonActions.fetchSuccess('getParentReportJobs'));
    } catch (error: any) {
        const errorMessage = getErrorMessage(error);
        logger.error(error);
        yield put(
            sharedCommonActions.fetchError('getParentReportJobs', errorMessage),
        );
    }
}
function* getProducerReportJob(action: GetProducerReportJobAction) {
    try {
        yield put(sharedCommonActions.fetchStart('getProducerReportJob'));
        const { token }: getCurrentUserResponse = yield call(
            firestoreAuthService.getCurrentUser,
        );

        const producerReportJob: { [producer_ref: string]: ProducerReportJob } =
            yield call(
                pixagriReportApiService.getProducerReportJobs.bind(
                    pixagriReportApiService,
                ),
                token.token,
                action.payload.campaignId,
                action.payload.type,
                action.payload.start_date,
                action.payload.end_date,
            );
        yield put(
            sharedReportActions.getProducerReportJobSuccess(producerReportJob),
        );
        yield put(sharedCommonActions.fetchSuccess('getProducerReportJob'));
    } catch (error: any) {
        const errorMessage = getErrorMessage(error);
        logger.error(error);
        yield put(
            sharedCommonActions.fetchError('getProducerReportJob', errorMessage),
        );
    }
}

function* generateNotification(action: GenerateNotificationAction) {
    try {
        yield put(sharedCommonActions.fetchStart('generateNotification'));
        const { token }: getCurrentUserResponse = yield call(
            firestoreAuthService.getCurrentUser,
        );
        const { reportJob } = action.payload;

        yield call(
            pixagriReportApiService.generateNotification.bind(
                pixagriReportApiService,
            ),
            token.token,
            reportJob,
        );

        yield put(sharedCommonActions.fetchSuccess('generateNotification'));
    } catch (error) {
        const errorMessage = getErrorMessage(error);
        logger.error(errorMessage);
        yield put(
            sharedCommonActions.fetchError('generateNotification', errorMessage),
        );
    }
}

function* retryReportGeneration(action: RetryReportGenerationAction) {
    try {
        yield put(sharedCommonActions.fetchStart('retryReportGeneration'));
        const { reportId } = action.payload;
        const { token }: getCurrentUserResponse = yield call(
            firestoreAuthService.getCurrentUser,
        );
        const reportJob: ReportJob = yield call(
            //@ts-ignore
            pixagriReportApiService.retryReportGeneration.bind(
                pixagriReportApiService,
            ),
            token.token,
            getBusinessLine(),
            reportId,
        );
        yield put(
            sharedReportActions.loadReports(
                parseFloat(reportJob.campaign_id),
                reportJob.type,
            ),
        );
        yield put(sharedCommonActions.fetchSuccess('retryReportGeneration'));
    } catch (error) {
        const errorMessage = getErrorMessage(error);
        logger.error(errorMessage);
        yield put(
            sharedCommonActions.fetchError('retryReportGeneration', errorMessage),
        );
    }
}
//Sends api request to Redux store
function* loadReportsConfig() {
    try {
        yield put(sharedCommonActions.fetchStart('loadReportsConfig'));

        const { token }: getCurrentUserResponse = yield call(
            firestoreAuthService.getCurrentUser,
        );
        const reportConfigurations: {
            [reportConfigurationRef: string]: ReportConfiguration;
        } = yield call(
            pixagriReportApiService.getReportConfigurations.bind(
                pixagriReportApiService,
            ),
            token.token,
        );

        if (!reportConfigurations) {
            throw new Error('Failed to load reports config');
        }

        yield put(actions.loadReportsConfigsSuccess(reportConfigurations));
        yield put(sharedCommonActions.fetchSuccess('loadReportsConfig'));
    } catch (error: any) {
        const errorMessage = getErrorMessage(error);
        yield put(
            sharedCommonActions.fetchError('loadReportsConfig', errorMessage),
        );
    }
}

function* loadReports(action: LoadReportsAction) {
    try {
        yield put(sharedCommonActions.fetchStart('loadReports'));
        const { campaignId, type, producer_ref, limit, date, start_date, end_date } =
            action.payload;
        const { token }: getCurrentUserResponse = yield call(
            firestoreAuthService.getCurrentUser,
        );

        const reports: {
            [ref: string]: ReportJob;
        } = yield call(
            pixagriReportApiService.getReportJobs.bind(pixagriReportApiService),
            token.token,
            campaignId,
            type,
            start_date,
            end_date,
            producer_ref,
            limit,
            date,
        );

        if (!reports) {
            throw new Error('Failed to load reports');
        }

        yield put(actions.loadReportsSuccess(reports));
        yield put(sharedCommonActions.fetchSuccess('loadReports'));
    } catch (error: any) {
        const errorMessage = getErrorMessage(error);
        yield put(sharedCommonActions.fetchError('loadReports', errorMessage));
    }
}

function* setSelectedReport(report: ReportJob) {
    try {
        yield put(sharedCommonActions.fetchStart('setSelectedReport'));
        yield put(actions.setSelectedReport(report));
        yield put(sharedCommonActions.fetchSuccess('setSelectedReport'));
    } catch (error: any) {
        const errorMessage = getErrorMessage(error);
        yield put(sharedCommonActions.fetchError('loadReports', errorMessage));
    }
}
